import { isEmpty } from '@shared/common'
import {
  AvailabilityGroupClientDto,
  BookingSearchByDaysClientDto,
  BookingSearchClientDto,
} from 'service-api/src/types/api'
import { waitlistEligible } from '../shared/offers'

/**
 * True when the unavailable reason was set by the Hatch overlay engine.
 * The DTO type does not declare `source`, so this encapsulates the cast.
 */
export const isHatchSourced = (reason: unknown): boolean =>
  (reason as { source?: string } | null | undefined)?.source === 'hatch'

export const getGroupMinimumPrice = (
  availabilityGroups: AvailabilityGroupClientDto[][] | undefined
): AvailabilityGroupClientDto => {
  if (!availabilityGroups || availabilityGroups.length == 0 || availabilityGroups[0].length == 0) {
    return {}
  }

  return availabilityGroups.reduce(
    (acc, group) => {
      const offerGroup = group[0].availabilities![0]!
      const accGroup = acc.availabilities![0]!.price!.value

      // We do not want to show price if unavailableReason is MBV
      if (offerGroup.unavailableReason?.type === 'PRICE' || isEmpty(offerGroup.price?.value)) {
        return acc
      }
      return accGroup === undefined || offerGroup.price!.value! < accGroup ? group[0] : acc
    },
    { availabilities: [{ price: { value: undefined } }] } as AvailabilityGroupClientDto
  )
}

export const getBaseGroup = (
  serviceTypeName: string,
  petGroup: number,
  availabilityGroups: AvailabilityGroupClientDto[] | AvailabilityGroupClientDto[][] | undefined
): AvailabilityGroupClientDto => {
  if (!availabilityGroups) {
    return {}
  }

  if (serviceTypeName === 'daycare') {
    const tempTestRequiredOffers = (availabilityGroups as AvailabilityGroupClientDto[][]).filter(
      (ag) =>
        ag?.[0]?.availabilities?.[0].unavailableReason?.type === 'TEMPER_TEST' ||
        ag?.[0]?.availabilities?.[0].condition?.reason?.type === 'TEMPER_TEST'
    )
    const applicableOffers = tempTestRequiredOffers.length > 0 ? tempTestRequiredOffers : availabilityGroups
    return getGroupMinimumPrice(applicableOffers as AvailabilityGroupClientDto[][])
  }

  return (availabilityGroups as AvailabilityGroupClientDto[])?.[petGroup] || {}
}

export const isDaycareOfferUnavailable = (availabilityGroups: AvailabilityGroupClientDto[][] | undefined) => {
  let allDaysUnavailable = true,
    atLeastOneUnavailableReasonApplies = false
  availabilityGroups?.forEach((group) => {
    const availability = group?.[0]?.availabilities?.[0]
    if (availability?.available) {
      allDaysUnavailable = false
    }
    if (
      isHatchSourced(availability?.unavailableReason) ||
      ['WEIGHT', 'TOTAL_WEIGHT', 'PET_COUNT', 'SALES_CHANNEL', 'OCCUPANCY'].includes(
        availability?.unavailableReason?.type || ''
      ) ||
      (availability?.unavailableReason?.type === 'TEMPER_TEST' &&
        availability.condition?.reason?.type !== 'TEMPER_TEST')
    ) {
      atLeastOneUnavailableReasonApplies = true
    }
  })
  return allDaysUnavailable && atLeastOneUnavailableReasonApplies
}

export const isDaycareOfferAvailableWithTempTest = (availabilityGroups: AvailabilityGroupClientDto[][] | undefined) => {
  return availabilityGroups?.some(
    (ag) =>
      (ag?.[0]?.availabilities?.[0].unavailableReason?.type === 'TEMPER_TEST' &&
        ag?.[0]?.availabilities?.[0].unavailableReason?.contingent) ||
      ag?.[0]?.availabilities?.[0].condition?.reason?.type === 'TEMPER_TEST'
  )
}

export const isDaycareOfferUnavailableWithTempTestFail = (
  availabilityGroups: AvailabilityGroupClientDto[][] | undefined
) => {
  return availabilityGroups?.some(
    (ag) =>
      ag?.[0]?.availabilities?.[0].unavailableReason?.type === 'TEMPER_TEST' &&
      ag?.[0]?.availabilities?.[0].unavailableReason?.temperTestFailed
  )
}

export const getRoomPrice = (
  availabilityGroup: AvailabilityGroupClientDto,
  isDaycare: boolean,
  totalPets: number | undefined
) => {
  const room = availabilityGroup.availabilities?.[0]
  if (!room || room.unavailableReason?.type === 'PRICE') {
    // We do not want to show price if unavailableReason is MBV
    return undefined
  }

  if (isDaycare) {
    return isEmpty(room.price?.value) ? undefined : room.price!.value! / (totalPets ?? 1)
  }

  return room.price?.rate
}

export const getTemperTestBadgeText = (
  availabilityGroups: AvailabilityGroupClientDto[] | AvailabilityGroupClientDto[][],
  isDaycare: boolean
): string | undefined | null => {
  const thisGroup: AvailabilityGroupClientDto = isDaycare
    ? (availabilityGroups.flat().filter((g) => g?.availabilities?.[0].temperTestMsg)[0] as AvailabilityGroupClientDto)
    : (availabilityGroups[0] as AvailabilityGroupClientDto)

  return thisGroup?.availabilities?.[0].temperTestMsg
}

/**
 * Determines whether a single booking search result can be acted upon by the
 * pet owner, i.e. the offer can be booked directly, scheduled with a temper
 * test, or joined via a waitlist. This mirrors the availability logic used to
 * render each OfferCard so that page-level decisions stay in sync with what the
 * cards show.
 */
export const isSearchResultActionable = (
  searchResult: BookingSearchClientDto | BookingSearchByDaysClientDto,
  serviceTypeName: string,
  petGroup: number,
  options?: { waitlistEnabled?: boolean }
): boolean => {
  const isDayCare = serviceTypeName === 'daycare'
  const isPackages = serviceTypeName === 'packages'

  const availabilityGroups =
    (searchResult as BookingSearchClientDto).availabilityGroups ||
    (searchResult as BookingSearchByDaysClientDto).availabilityGroupsByDays ||
    []

  const group = getBaseGroup(serviceTypeName, petGroup, availabilityGroups)
  const room = group.availabilities?.[0] || {}

  const isOfferAvailable = isDayCare
    ? !isDaycareOfferUnavailable(availabilityGroups as AvailabilityGroupClientDto[][] | undefined)
    : isPackages || !!room.available

  const isOfferAvailableWithTempTest = isDayCare
    ? isDaycareOfferAvailableWithTempTest(availabilityGroups as AvailabilityGroupClientDto[][] | undefined)
    : (room.unavailableReason?.type === 'TEMPER_TEST' && room.unavailableReason?.contingent) ||
      (room.condition?.reason?.type === 'TEMPER_TEST' && !isHatchSourced(room.unavailableReason))

  const isOfferUnavailableWithTempTestFail = isDayCare
    ? isDaycareOfferUnavailableWithTempTestFail(availabilityGroups as AvailabilityGroupClientDto[][] | undefined)
    : room.unavailableReason?.type === 'TEMPER_TEST' && room.unavailableReason?.temperTestFailed

  const canProceedWithOffer = (isOfferAvailable || isOfferAvailableWithTempTest) && !isOfferUnavailableWithTempTestFail

  if (canProceedWithOffer) return true

  // When waitlist is enabled the owner can still take action on an unavailable
  // offer by joining the waitlist, so it should not count as fully unavailable.
  return !!(options?.waitlistEnabled && waitlistEligible(group.availabilities))
}

/**
 * True when the search returned no offers at all, or returned offers where
 * none can be booked, temper-test scheduled, or waitlisted. Used to redirect
 * the pet owner to a "Contact Us To Book" page.
 */
export const areAllOffersUnavailable = (
  offers: (BookingSearchClientDto | BookingSearchByDaysClientDto)[] | undefined,
  serviceTypeName: string,
  petGroup: number,
  options?: { waitlistEnabled?: boolean }
): boolean => {
  if (!offers) return false
  return offers.every((offer) => !isSearchResultActionable(offer, serviceTypeName, petGroup, options))
}

/**
 * True when a grooming offer has at least one available day/slot. Grooming
 * results expose availability at the day level via `availabilityGroupsByDays`,
 * so an offer is actionable when any availability across any day is available.
 */
export const isGroomingOfferActionable = (groom: BookingSearchByDaysClientDto): boolean => {
  return !!groom.availabilityGroupsByDays?.some((group) =>
    group?.some((ag) => ag?.availabilities?.some((availability) => availability?.available))
  )
}

/**
 * True when the grooming search returned no offers at all, or returned offers
 * where none of the groom offers has any available day/slot (e.g. every day is
 * blocked out). Used to redirect the pet owner to a "Contact Us To Book" page
 * for the grooming flow.
 */
export const areAllGroomingOffersUnavailable = (offers: BookingSearchByDaysClientDto[] | undefined): boolean => {
  if (!offers) return false
  return offers.every((groom) => !isGroomingOfferActionable(groom))
}
